/**
 * useDuplicateProperties Hook (Cloud Function Version)
 *
 * Uses pre-computed duplicate data from Cloud Functions for scalability.
 * Reads cached metadata and triggers server-side duplicate detection.
 */

import { useState, useCallback, useEffect } from "react";
import { collection, getDocs, doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useToast } from "../components/shared";

export const useDuplicateProperties = () => {
  const toast = useToast();
  const [duplicateGroups, setDuplicateGroups] = useState([]);
  const [metadata, setMetadata] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState(false);

  /**
   * Load cached duplicate metadata on mount
   */
  useEffect(() => {
    loadMetadata();
  }, []);

  /**
   * Load duplicate metadata from Firestore cache
   */
  const loadMetadata = useCallback(async () => {
    try {
      const metaDoc = await getDoc(doc(db, "system_metadata", "duplicates"));
      if (metaDoc.exists()) {
        const data = metaDoc.data();
        setMetadata(data);
        setChecked(true);
        return data;
      }
      return null;
    } catch (error) {
      console.error("Error loading duplicate metadata:", error);
      return null;
    }
  }, []);

  /**
   * Trigger cloud function to detect duplicates
   * Updates the cache and returns fresh results
   */
  const findDuplicates = useCallback(async () => {
    try {
      setLoading(true);
      toast.info("Scanning for duplicates... This may take a moment.");

      const functions = getFunctions(undefined, "europe-west2");
      const detectDuplicatesFunc = httpsCallable(functions, "detectDuplicates");

      const result = await detectDuplicatesFunc();
      const data = result.data;

      toast.success(
        `Found ${data.totalGroups} duplicate group${data.totalGroups !== 1 ? 's' : ''} (${data.totalDuplicates} properties)`
      );

      // Reload metadata
      const freshMeta = await loadMetadata();
      setMetadata(freshMeta);
      setChecked(true);

      return data;
    } catch (error) {
      console.error("Error detecting duplicates:", error);
      toast.error("Failed to detect duplicates: " + error.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [toast, loadMetadata]);

  /**
   * Load full duplicate groups for display in modal
   */
  const loadDuplicateGroups = useCallback(async () => {
    try {
      const groupsSnapshot = await getDocs(collection(db, "duplicate_groups"));
      const groups = groupsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Transform to match expected format for DuplicateMergeModal
      const formattedGroups = groups.map((group) => ({
        hash: group.hash,
        address: group.address,
        postcode: group.postcode,
        properties: [], // Properties will be fetched by the modal if needed
        propertyIds: group.propertyIds,
        canonicalId: group.canonicalId,
      }));

      setDuplicateGroups(formattedGroups);
      return formattedGroups;
    } catch (error) {
      console.error("Error loading duplicate groups:", error);
      toast.error("Failed to load duplicate groups");
      return [];
    }
  }, [toast]);

  return {
    duplicateGroups,
    metadata,
    loading,
    checked,
    findDuplicates,
    loadDuplicateGroups,
    loadMetadata,
    hasDuplicates: metadata?.totalGroups > 0,
    totalDuplicates: metadata?.totalDuplicates || 0,
    totalGroups: metadata?.totalGroups || 0,
  };
};
